import React from 'react';

import { Icon } from '@column/column-ui-kit';

import { TimelineStatus } from '~/app/pages/Transfers/_components/Timeline/types';
import { ROUTE } from '~/app/routes';
import { TransferParty, TransferPartyDetail } from '~/components/ObjectCards';
import { TimelineEventStatus, TimelineEventsEntry } from '~/components/TimelineEvents';
import { BankAccount } from '~/repositories/BankAccountRepository';
import { InterestPayout } from '~/repositories/InterestRepository';

export const buildInterestPayoutTimelineEvents = ({
  payout,
  bankAccount,
  entityType,
  breakdownComponent,
  navigate,
}: {
  payout: InterestPayout;
  bankAccount?: BankAccount;
  entityType?: 'person' | 'business';
  breakdownComponent?: React.ReactNode;
  navigate?: (path: string) => void;
}): TimelineEventsEntry[] => {
  const events: TimelineEventsEntry[] = [];

  if (breakdownComponent) {
    events.push({
      label: 'Interest Breakdown',
      icon: <Icon.CirclePercent />,
      status: TimelineEventStatus.Success,
      content: breakdownComponent,
    });
  }

  const payoutAmount = payout.amount
    ? typeof payout.amount === 'string'
      ? parseFloat(payout.amount)
      : payout.amount
    : 0;
  const absoluteAmount = Math.abs(payoutAmount);

  const getPartyIcon = () => {
    if (entityType === 'person') return <Icon.User />;
    if (entityType === 'business') return <Icon.Business />;
    return <Icon.Bank />;
  };

  const buildBankAccountCard = (): React.ReactNode | undefined => {
    if (!bankAccount) return undefined;

    const details: TransferPartyDetail[] = [];
    if (bankAccount.id) {
      details.push({ label: 'Bank Account ID', value: bankAccount.id, isMonospaced: true });
    }
    const routingNumber = (bankAccount.defaultAccountNumber as any)?.routingNumber || bankAccount.routingNumber;
    if (routingNumber) {
      details.push({ label: 'Routing Number', value: routingNumber });
    }

    return (
      <TransferParty
        icon={getPartyIcon()}
        title={bankAccount.displayName || bankAccount.description || 'Unnamed Bank Account'}
        amount={
          payout.amount
            ? {
                value: absoluteAmount,
                currencyCode: payout.currencyCode || 'USD',
                isAdding: true,
                partyType: 'receiver',
              }
            : undefined
        }
        details={details.length > 0 ? details : undefined}
        metadata={payout.type ? payout.type.charAt(0).toUpperCase() + payout.type.slice(1).toLowerCase() : undefined}
        onClick={
          navigate && bankAccount.id ? () => navigate(`${ROUTE.BANK_ACCOUNTS}/edit/${bankAccount.id}`) : undefined
        }
        status={TimelineStatus.Success}
      />
    );
  };

  events.push({
    label: 'Completed',
    date: payout.updatedAt ? new Date(payout.updatedAt) : undefined,
    status: TimelineEventStatus.Success,
    content: buildBankAccountCard(),
  });

  return events;
};
